import {useCallback} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import CheckIcon from "@mui/icons-material/Check";
import {getFormattedProperty} from "../../utils/utils";
import {StyledButtonOutlined} from "../../utils/uiUtils";
import {UserBadge} from "../user/UserBadge";
import {AddressBadge} from "../address/AddressBadge";

const stateMnemonicToName = {
    "preparing_for_shipment": "Preparing For Shipment",
    "courier_en_route": "Courier En route",
    "shipment_collected": "Shipment Collected",
    "out_for_delivery": "Out For Delivery",
    "verified_and_completed": "Verified & Completed",
    "audit_started": "Audit Started",
    "audit_complete": "Audit Complete",
    "ingestion_started": "Ingestion Started",
    "ingestion_complete": "Ingestion Complete"
};

function getAddressDetails(shipments, currentState) {
    let _shipments = JSON.parse(JSON.stringify(shipments));
    _shipments = _shipments.filter((element) => currentState === element.status);
    if (_shipments?.length) {
        return _shipments.map((element) => ({
            ...element,
            location: element.shipping_address?.address_line_1,
            city: `${element.shipping_address?.city} (${element.shipping_address?.state})`,
            pinCode: element.shipping_address?.postcode,
            status: "50",
            name: element?.first_name + " " + element?.last_name
        }));
    }
    return undefined;
}

function getDetailedOrderSteps(orderDetail) {
    if (orderDetail?._order_states.length) {
        let currentIndex = orderDetail._order_states
            .map((orderState) => orderState.status.toLowerCase())
            .indexOf(orderDetail.status.toLowerCase());
        return orderDetail._order_states.map((orderState, index) => ({
            ...orderState,
            id: index,
            label: stateMnemonicToName[orderState.status] || orderState.status,
            description: orderState.message,
            date: orderState?.timestamp && getFormattedProperty(orderState, "timestamp", "datetime")
                ? getFormattedProperty(orderState, "timestamp", "datetime")
                : orderState?.timestamp,
            status: currentIndex > index || orderDetail.status.toLowerCase() === "verified_and_completed"
                ? "completed"
                : currentIndex === index ? "pending" : "disabled",
            deliveryLocations: orderDetail?.shipments?.length
                ? getAddressDetails(orderDetail.shipments, orderState.status)
                : undefined,
            isVisible: !["audit_started", "audit_complete", "ingestion_started", "ingestion_complete"].includes(orderState.status)
        }));
    }
    return [];
}

const mapShippingModeText = (mode) => ({
    'self': 'Self', 'tapeark': 'Tape Ark', 'none': 'External'
}[mode]);

export function VerticalLinearStepper({orderDetail}) {
    if (orderDetail?.["order_number"]) {
        orderDetail["_order_states"] = JSON.parse(JSON.stringify(orderDetail.order_states));
        orderDetail["_order_states"].reverse();
    }
    const detailedOrderSteps = orderDetail?.["order_number"] ? getDetailedOrderSteps(orderDetail) : [];

    const openTrackingPage = (location) => {
        const linksMap = {
            dhl: "https://www.dhl.com/in-en/home/tracking.html",
            fedex: "https://www.fedex.com/en-in/tracking.html",
            ups: "https://www.ups.com/track?loc=en_IN&requester=ST/",
            mnx: "https://www.mnx.com/tracking/"
        };
        const linkToUse = location.tracking_link ? location.tracking_link : linksMap[String(location.courier_partner)];
        window.open(linkToUse, "_blank");
    };

    const getStepLabel = useCallback((step) => {
        const iconsMap = {
            completed: () => (<div className="verticalStepperIcon_Completed"><CheckIcon/></div>),
            pending:   () => (<div className="verticalStepperIcon_Inprogress"></div>),
            disabled:  () => (<div className="verticalStepperIcon_Disabled"></div>)
        };
        const stepLabelInternals = () => (
            <>
                <span className={step.status === "disabled" ? "verticalStepLabelDisabled" : "verticalStepLabel"}>
                    {step.label}
                </span>
                <br/>
                <small className="dateLabel">{step.date}</small>
            </>
        );
        return (
            <StepLabel StepIconComponent={iconsMap[step.status]}>
                {stepLabelInternals()}
            </StepLabel>
        );
    }, []);

    const getStepContent = useCallback((step) => {
        const multipleLocations = step?.deliveryLocations?.length > 1;

        const getLocationComponent = (location, indexOfLocation) => (
            <Box key={`location_content${step.key}:${location.uuid}-${indexOfLocation}`}
                 sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                {multipleLocations && (
                    <div className="locationIndex">
                        <div className="locationIndexText" style={{width: 28, height: 28, fontSize: 12}}>{indexOfLocation + 1}</div>
                    </div>
                )}
                {['self', 'none'].includes(orderDetail?.shipping_mode) ? (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <span className="deliveryType">Shipping Mode: </span>
                        <span className="deliveryType">{mapShippingModeText(orderDetail.shipping_mode)}</span>
                    </Box>
                ) : (
                    <>
                        <Typography sx={{color: '#BABABA', fontSize: 12, fontFamily: 'Poppins', whiteSpace: 'nowrap'}}>Contact</Typography>
                        <Box sx={{width: 250}}>
                            <UserBadge
                                value={{
                                    first_name:    location.first_name,
                                    last_name:     location.last_name,
                                    phone_number:  location.phone_number,
                                    mobile_number: location.mobile_number,
                                    email:         location.optional_email,
                                }}
                                canToggle={false}
                                size="sm"
                            />
                        </Box>
                        {location.shipping_address && (
                            <>
                            <Typography sx={{color: '#BABABA', fontSize: 12, fontFamily: 'Poppins', whiteSpace: 'nowrap'}}>Shipping Address</Typography>
                            <Box sx={{width: 250}}>
                                <AddressBadge
                                    value={{
                                        address_line_1: location.shipping_address.address_line_1,
                                        address_line_2: location.shipping_address.address_line_2,
                                        postal_code:    location.shipping_address.postal_code,
                                        city:           location.shipping_address.city,
                                        state:          location.shipping_address.state,
                                        country:        location.shipping_address.country,
                                    }}
                                    fields={["address_line_1", "postal_code", "city", "state", "country"]}
                                    canToggle={false}
                                />
                            </Box>
                            </>
                        )}
                        <StyledButtonOutlined
                            variant="outlined"
                            className="deliveryLocationButton"
                            style={{textTransform: "none"}}
                            onClick={() => openTrackingPage(location)}
                        >
                            Track
                        </StyledButtonOutlined>
                    </>
                )}
            </Box>
        );

        return (
            <StepContent>
                <span className="stepDescription">{step.description}</span>
                {step?.deliveryLocations?.length > 0 && (
                    <Box sx={{mb: 2}}>
                        <div className="deliveryLocationsContainer">
                            <h4 className="deliveryLocationsHeadingText">Shipment</h4>
                            <Stack spacing={2} divider={<Divider flexItem/>}>
                                {step.deliveryLocations.map((location, i) => getLocationComponent(location, i))}
                            </Stack>
                        </div>
                    </Box>
                )}
            </StepContent>
        );
    }, [orderDetail]);

    const getStepComponent = useCallback((step, index) => (
        <Step active expanded key={step.id}
              className={step.status !== "disabled" ? "activeVerticalBar" : "disabledVerticalBar"}>
            {getStepLabel(step, index)}
            {getStepContent(step)}
        </Step>
    ), [getStepLabel, getStepContent]);

    return (
        <Box>
            <Stepper activeStep={1} orientation="vertical" sx={{m: 4}}>
                {detailedOrderSteps.map((step, index) => step.isVisible && getStepComponent(step, index))}
            </Stepper>
        </Box>
    );
}
